import { Input, Radio, Table } from 'antd';
import { useElementSize } from 'hooks/useElementSize';
import useEntities from 'hooks/useEntities';
import { useEffect, useMemo, useState, useRef } from 'react';
import { removeAccents } from 'utils/oneLiners';
import useIsMobile from 'hooks/useIsMobile';
import { entityColumnsToAntColumns } from 'utils/entityColumnsToAntColumns';
import { Tooltip, Tag } from 'antd';

const PlayerSelectionTable = ({
  readonly = false,
  dataSource,
  showTeamsColumn = true,
  selectedRowKeys = [],
  onChange,
  style,
  className,
  registrationData = null, // This will now be the players data with registrations
  header = null,
  selectedSeasonIds = [],
  ...tableProps
}) => {
  const entities = useEntities();
  const entity = entities.Player;
  const [search, setSearch] = useState('');
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [sortInfo, setSortInfo] = useState({ columnKey: null, order: null });
  const [onlySelected, setOnlySelected] = useState(false);
  const containerRef = useRef(null);
  const tableRef = useRef(null);
  const { height: containerHeight } = useElementSize({ ref: containerRef });
  const { height: headerHeight } = useElementSize({ ref: tableRef, selector: "th" });
  const isMobile = useIsMobile();

  // Filter players based on search
  useEffect(() => {
    if (!dataSource) {
      setFilteredPlayers([]);
      return;
    }

    let filtered = dataSource;

    // Apply search filter
    if (search.trim() !== '') {
      const searchWords = removeAccents(search.trim().toUpperCase()).split(' ').map(w => w.trim());

      filtered = filtered.filter(player => {
        // Create search index for each player
        const searchIndex = removeAccents([
          player.firstName || '',
          player.lastName || '',
          player.email || '',
          player.User ? 'has account' : 'no account',
          player.ListPlayerLevel?.name || '',
          ...(player.ListPositions || []).map(p => p.name || ''),
          ...(showTeamsColumn && player.Teams ? player.Teams.map(t => t.name || '') : []),
          // Add active seasons to search
          ...(player.SeasonPlayerRegistrations ?
            player.SeasonPlayerRegistrations
              .filter(reg => reg.Season && ['OPEN_FOR_REGISTRATION', 'RUNNING'].includes(reg.Season.status))
              .map(reg => reg.Season.name)
              .filter((name, index, arr) => arr.indexOf(name) === index) // Remove duplicates
            : []
          )
        ].join(' ').toUpperCase());

        // Check if all search words are found in the search index
        for (const searchWord of searchWords) {
          if (searchIndex.indexOf(searchWord) === -1) {
            return false;
          }
        }
        return true;
      });
    }

    setFilteredPlayers(filtered);
  }, [dataSource, search, showTeamsColumn]);

  function renderMobileRow(record) {
    return (
      <div className="flex flex-col gap-2">
        <div className="text-sm text-gray-600 flex flex-wrap gap-2 items-center">
          {entity.columns['NameWithLogo']?.render(record)}
          {entity.columns['LevelTag']?.render(record)}
          <span className="ml-auto">
            {entity.columns['TeamsShort_Mobile_5']?.render(record)}
          </span>
        </div>
        {hasActiveSeasonRegistrations(selectedSeasonIds, record) && (
          <div className="flex justify-center">
            {getPlayerSeasonRegistrationStatusColumn(selectedSeasonIds)?.render(record)}
          </div>
        )}
      </div>
    )
  }

  const columns = useMemo(() => {
    if (isMobile) {
      return [
        {
          title: 'Season',
          render: (record) => {
            return (
              renderMobileRow(record)
            )
          }
        }
      ]
    } else {
      const base = createPlayerTableColumns(entities, {
        showTeamsColumn,
        sortInfo,
        includeSorting: true,
        selectedSeasonIds
      });

      return base;
    }
  }, [showTeamsColumn, sortInfo, entities]);

  // Table onChange handler for sorting
  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter && sorter.columnKey && sorter.order) {
      setSortInfo({ columnKey: sorter.columnKey, order: sorter.order });
    } else {
      setSortInfo({ columnKey: null, order: null });
    }
  };

  // Function to toggle row selection
  const toggleRowSelected = (record) => {
    const playerId = record.playerId;
    const isCurrentlySelected = selectedRowKeys.includes(playerId);
    if (isCurrentlySelected) {
      const newSelectedKeys = selectedRowKeys.filter(key => key !== playerId);
      const selectedPlayers = dataSource.filter(player => newSelectedKeys.includes(player.playerId));
      onChange(newSelectedKeys, selectedPlayers);
    } else {
      const newSelectedKeys = [...selectedRowKeys, playerId];
      const selectedPlayers = dataSource.filter(player => newSelectedKeys.includes(player.playerId));
      onChange(newSelectedKeys, selectedPlayers);
    }
  };

  return (
    <div style={style} className={'h-full flex flex-col ' + className}>
      {!readonly && (
        <>
          <div className={`flex gap-3 ${isMobile ? 'flex-col items-center' : 'items-center justify-between'}`}>
            {!isMobile &&
              <div>
                <Radio.Group onChange={(e) => setOnlySelected(e.target.value)} value={onlySelected} size="small">
                  <Radio.Button value={false}>All</Radio.Button>
                  <Radio.Button value={true}>Selected ({selectedRowKeys.length})</Radio.Button>
                </Radio.Group>
              </div>
            }
            <div className="flex justify-center items-center">
              {header}
            </div>
            <div>
              <span className="whitespace-nowrap mr-2">
                Players: <span className="font-bold">{filteredPlayers?.length?.toLocaleString() ?? 0}</span>
              </span>
              <Input
                className="no-hightlight-on-focus"
                placeholder="Search players..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear={true}
                size="small"
                style={{ width: 180 }}
              />
            </div>
          </div>
          <div className="mt-4"></div>
        </>
      )}
      <div className="flex-1 overflow-hidden" ref={containerRef}>
        {(!dataSource) ? (
          <div className="flex items-center justify-center h-full">No data source provided.</div>
        ) : (
          <Table
            ref={tableRef}
            rowKey="playerId"
            columns={columns}
            dataSource={onlySelected ? filteredPlayers.filter(player => selectedRowKeys.includes(player.playerId)) : filteredPlayers}
            rowSelection={!readonly && {
              selectedRowKeys,
              onChange: (keys, rows) => {
                const visiblePlayerIds = filteredPlayers.map(player => player.playerId);
                const previouslySelectedNotVisible = selectedRowKeys.filter(key => !visiblePlayerIds.includes(key));
                const newSelectedKeys = [...previouslySelectedNotVisible, ...keys];
                const selectedPlayers = dataSource.filter(player => newSelectedKeys.includes(player.playerId));
                onChange(newSelectedKeys, selectedPlayers);
              },
              type: 'checkbox',
            }}
            onRow={(record) => (!readonly && {
              onClick: () => {
                toggleRowSelected(record);
              },
              style: { cursor: 'pointer' }
            })}
            pagination={false}
            size="small"
            onChange={handleTableChange}
            scroll={{ y: containerHeight - headerHeight }}
            {...tableProps}
          />
        )}
      </div>
    </div>
  );
};

export default PlayerSelectionTable; 



export const createPlayerTableColumns = (entities, options = {}) => {
  const { showTeamsColumn = true, sortInfo = null, includeSorting = false, selectedSeasonIds = [] } = options;

  const base = [
    ...entityColumnsToAntColumns(entities.Player, ['NameWithLogoAndEmail']).map(col => ({
      ...col,
      key: 'NameWithLogoAndEmail',
      width: 180,
      ...(includeSorting && sortInfo && { sortOrder: sortInfo.columnKey === 'NameWithLogo' ? sortInfo.order : null }),
    })),
    ...entityColumnsToAntColumns(entities.Player, ['LevelTag']).map(col => ({
      ...col,
      key: 'LevelTag',
      width: 40,
      sortDirections: [],
      ...(includeSorting && sortInfo && { sortOrder: sortInfo.columnKey === 'LevelTag' ? sortInfo.order : null }),
    })),
    ...entityColumnsToAntColumns(entities.Player, ['Positions']).map(col => ({
      ...col,
      key: 'Positions',
      width: 120,
      ...(includeSorting && sortInfo && { sortOrder: sortInfo.columnKey === 'Positions' ? sortInfo.order : null }),
    })),
    ...entityColumnsToAntColumns(entities.Player, ['MemberOfHms']).map(col => ({
      ...col,
      title: 'HMS account',
      key: 'userAccount',
      width: 80,
      ...(includeSorting && sortInfo && {
        sorter: (a, b) => {
          const aHasUser = a.User ? 1 : 0;
          const bHasUser = b.User ? 1 : 0;
          return aHasUser - bHasUser;
        },
        sortOrder: sortInfo.columnKey === 'userAccount' ? sortInfo.order : null
      }),
    })),
  ];

  if (showTeamsColumn) {
    base.push(getPlayerSeasonRegistrationStatusColumn(selectedSeasonIds));

    base.push(
      entityColumnsToAntColumns(entities.Player, ['TeamsShort'])[0]
    );
  }

  return base;
};

function getActiveSeasonRegistrations(selectedSeasonIds, player) {
  const playerRegistrations = player.SeasonPlayerRegistrations || [];
  const activeSeasonRegistrations = playerRegistrations
    .filter(reg => selectedSeasonIds.includes(reg.seasonId))
  return activeSeasonRegistrations;
}

function hasActiveSeasonRegistrations(selectedSeasonIds, player) {
  const activeSeasonRegistrations = getActiveSeasonRegistrations(selectedSeasonIds, player);
  return activeSeasonRegistrations.length > 0;
}

function getPlayerSeasonRegistrationStatusColumn(selectedSeasonIds) {
  return {
    title: 'Registration status',
    key: 'activeSeasons',
    width: 160,
    render: (player) => {
      const activeSeasonRegistrations = getActiveSeasonRegistrations(selectedSeasonIds, player);

      return (
        <div className="">
          {activeSeasonRegistrations.length === 0 ? <Tooltip title="This player has not been invited yet">
            <Tag className="mr-0">NOT INVITED</Tag>
          </Tooltip> : ''}

          {activeSeasonRegistrations.map((registration, index) => (
            <div key={index} className="flex items-center gap-2">
              {
                registration.isConfirmed == true ?
                  <Tooltip title="Player has been invited and accepted the invitation and paid. No invite will be sent.">
                    <Tag className="mr-0" color="green">CONFIRMED</Tag>
                  </Tooltip>
                : registration.isConfirmed == false && registration.isAccepted == false ?
                  <Tooltip title="Player has been invited but has not accepted the invitation yet. Invite will be sent again.">
                    <Tag className="mr-0" color="orange">WAITING FOR ACCEPTANCE</Tag>
                  </Tooltip>
                : registration.isConfirmed == false && registration.isAccepted == true ?
                  <Tooltip title="Player has been invited and accepted the invitation but has not paid yet. Invite will be sent again.">
                    <Tag className="mr-0" color="orange">WAITING FOR PAYMENT</Tag>
                  </Tooltip>
                : console.log('suspicious status', registration) ?? '???'
              }
            </div>
          ))}
        </div>
      );
    },
  }
}
